/* ────────────────────────────────────────
   cardFxEngine — 코스메틱 카드 이펙트 순수 엔진 (스펙 §16-2·§16-3 / 메모리 cosmetic-card-effect-system)
   - 입력(등급·슬러그·강화·시즌·seedId) → 출력 = 선언적 CardFxSpec. 렌더는 CosmeticCard.jsx가 소비.
   - 결정론: 같은 카드(seedId) = 같은 결·같은 파티클 배치. Math.random 0개 — mulberry32 시드 PRNG.
     (목업 roki-petal-card.html은 Math.random이라 새로고침마다 달랐음 → 엔진에서 결정론화)
   - 성능 예산(모바일 60fps)을 수치로 명시 + assertBudget으로 스펙 자체 검증(테스트에서 전 조합 강제).
   - DOM/document 접근 0 — 순수 함수만. TDD: cardFxEngine.test.ts
──────────────────────────────────────── */

export type Grade = 'N' | 'R' | 'E' | 'L';
export type FxTier = 'static' | 'holo' | 'prism';
export type PrismVariant = '' | 'v1' | 'v3' | 'v6';

/** 성능 예산 — 모바일 60fps(프레임 16.67ms) 기준. 위반은 assertBudget이 잡는다. */
export const PERF_BUDGET = {
  /** 60fps 프레임 예산(ms) = 1000/60 */
  frameMs: 1000 / 60,
  /** 카드 1장당 이펙트 레이어 DOM 상한 — 최대치 L v6 = 8과 등가 */
  maxFxLayers: 8,
  /** idle 시 동시 애니메이션 DOM 노드 상한 — 최대치 L v6 + 벚꽃 13 = 20과 등가 */
  maxAnimatedNodes: 20,
  /** 파티클 노드 상한 — 록이 확정값 13 < 16. 초과 선언은 절삭 */
  maxParticles: 16,
  /** blur() 상한(px) — paint 비용 폭증 방지. 현행 prism 레이어 1px만 허용 */
  maxBlurPx: 1,
  /** 무한 루프 애니 최소 주기(s) — 과도한 리페인트 방지 */
  minAnimDurS: 0.4,
} as const;

/* ── 카드 UUID → 결정적 시드 (기존 CosmeticCard.cardSeed 이동 — 해시 알고리즘 동일 유지) ── */
export interface CardSeed {
  angle: number; // 패싯 각도 0~89°
  hue: number;   // 색 위상 0~359°
  facet: number; // 패싯 크기 9~16px
  u: number;     // 원시 해시(파티클 PRNG 시드용)
}

export function cardSeed(id: string | number | null | undefined): CardSeed {
  let h = 0;
  const s = String(id || '');
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  const u = Math.abs(h);
  return {
    angle: u % 90,
    hue: (u >> 3) % 360,
    facet: 9 + ((u >> 7) % 8),
    u,
  };
}

/** mulberry32 — 32bit 시드 PRNG(0~1). 파티클 배치 결정론의 단일 출처 */
function mulberry32(a: number): () => number {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/* ── 레이어 테이블 — 등급 티어별 선언 (CSS 클래스는 CosmeticCard.jsx CSS와 1:1) ── */
export interface FxLayerSpec {
  kind: 'prism' | 'texture' | 'glitter' | 'sheen' | 'sheen2' | 'glare' | 'texlight' | 'sweep';
  className: string;
  z: number;
  /** 가독성 3원칙(§16-2) 중앙 마스크(fade-c) 적용 여부 */
  masked: boolean;
  /** idle 시 자체 무한 애니 여부(포인터 반응만 하는 레이어는 false) */
  animated: boolean;
}

const L = (kind: FxLayerSpec['kind'], z: number, masked: boolean, animated: boolean): FxLayerSpec =>
  ({ kind, className: kind, z, masked, animated });

/** 티어별 레이어 — 렌더 순서 = 배열 순서(기존 JSX 순서 보존) */
const TIER_LAYERS: Record<FxTier, FxLayerSpec[]> = {
  static: [],
  // E = 다운그레이드 홀로(§16-2): 글리터+시인+글레어
  holo: [L('glitter', 5, false, false), L('sheen', 4, true, true), L('glare', 6, false, false)],
  // L = 프리즘 풀셋. sheen2는 v6 전용이라 여기 제외 — resolve에서 조건 삽입
  prism: [
    L('prism', 4, true, false),
    L('texture', 9, true, false),
    L('glitter', 5, false, false),
    L('sheen', 4, true, true),
    L('glare', 6, false, false),
    L('texlight', 10, false, false),
    L('sweep', 7, false, true),
  ],
};

const SHEEN2 = L('sheen2', 5, true, true); // v6 풀포일 전용 역방향 스펙트럼

/* ── 시그니처 파티클 — 캐릭터 슬러그별 L 카드 전용 레지스트리 ──
   kind 열거 증식 대신 motion×shape 직교 분해(2026-07-05 설계 B안):
   새 캐릭터가 기존 조합을 쓰면 레지스트리 엔트리 1개 추가가 전부 — 엔진은 테이블 조회만. */

/** 이동 아키타입 — CosmeticCard @keyframes(cosc-fx-*)와 1:1.
 *  rise는 실수요 확인 전 보류(설계 §5 — YAGNI). */
export type ParticleMotion = 'fall' | 'drift';
/** 파티클 형태 — CSS 형태 클래스(sh-*)와 1:1.
 *  ⚠ CSS는 소비자(2호 캐릭터 튜너 확정값)와 함께 추가 — 현재 실장은 petal만(설계 §4 절제). */
export type ParticleShape = 'petal' | 'leaf' | 'coin' | 'spark';

/** 런타임 등록 집합 — assertBudget이 오타·미등록 값을 선언 단계에서 잡는다(JS 소비자 대비) */
export const REGISTERED_MOTIONS: readonly ParticleMotion[] = ['fall', 'drift'];
export const REGISTERED_SHAPES: readonly ParticleShape[] = ['petal', 'leaf', 'coin', 'spark'];

/** buildParticles 낙하 주기 기본 범위(s) — 현행 5.5 + rnd*4.5 유지 */
const DEFAULT_DUR_RANGE: [number, number] = [5.5, 10];

export interface SignatureFx {
  /** 레지스트리 라벨(디버그·테스트용) — 동작은 motion/shape가 결정 */
  kind: string;
  motion: ParticleMotion;
  shape: ParticleShape;
  /** 파티클 수 — PERF_BUDGET.maxParticles로 절삭됨 */
  count: number;
  speed: number;   // 낙하 속도 배율
  sizeMul: number; // 크기 배율
  sway: number;    // 흩날림 폭 배율
  opaMax: number;  // 최대 불투명도
  colors: [string, string][]; // 그라디언트 (시작, 끝) 페어 순환
  /** 주기 범위(s) — 미지정 시 [5.5, 10]. spark류 짧은 주기 대비 */
  durRange?: [number, number];
}

/** 록이 벚꽃 1호 — 2026-06-13 효신님 튜너 확정값 박제:
 *  꽃잎 13장 · 속도 1.00x · 꽃잎크기 50% · 흩날림폭 88% · 진하기 43%
 *  (mockups/sotda-cosmetic/roki-petal-card.html · 메모리 cosmetic-card-effect-system) */
export const SIGNATURE_FX: Record<string, SignatureFx> = {
  roki: {
    kind: 'petalfall', motion: 'fall', shape: 'petal',
    count: 13, speed: 1.0, sizeMul: 0.5, sway: 0.88, opaMax: 0.43,
    // 진한 벚꽃 핑크 — 라일락 배경 대비(연핑크는 묻혀서 폐기, 목업 COLORS와 동일)
    colors: [['#ff9ec4', '#ff6fa8'], ['#ffb3d0', '#ff7eb0'], ['#f9a0d8', '#e86fc0']],
  },
};

export interface ParticleSpec {
  xPct: number;   // 좌우 시작 위치 4~96%
  scale: number;  // 개별 크기 (0.7~1.6) × sizeMul
  durS: number;   // 낙하 주기 5.5~10s
  delayS: number; // 음수 딜레이 −9~0s = 처음부터 흩어져 있음
  c1: string;
  c2: string;
}

/** 시그니처 파티클 결정적 생성 — 같은 seedU = 같은 배치 */
export function buildParticles(sig: SignatureFx, seedU: number): ParticleSpec[] {
  const rnd = mulberry32((seedU ^ 0x9e3779b9) | 0);
  const n = Math.min(sig.count, PERF_BUDGET.maxParticles);
  const [durLo, durHi] = sig.durRange ?? DEFAULT_DUR_RANGE;
  const out: ParticleSpec[] = [];
  for (let i = 0; i < n; i++) {
    out.push({
      xPct: +(4 + rnd() * 92).toFixed(2),
      scale: +((0.7 + rnd() * 0.9) * sig.sizeMul).toFixed(2),
      durS: +(durLo + rnd() * (durHi - durLo)).toFixed(2),
      delayS: +(-rnd() * 9).toFixed(2),
      c1: sig.colors[i % sig.colors.length][0],
      c2: sig.colors[i % sig.colors.length][1],
    });
  }
  return out;
}

/* ── 스펙 산출 ── */
export interface CardFxInput {
  grade: Grade;
  slug: string;
  level?: number;    // 강화 +0~+10 (+10 = v6 풀포일 승격)
  season?: boolean;  // 시즌 한정 = v3 갤럭시
  seedId?: string;   // 카드 UUID — 미지정 시 `${grade}:${slug}` (기존 컴포넌트 동작 보존)
}

export interface CardFxSpec {
  tier: FxTier;
  variant: PrismVariant;
  prismBorder: boolean;      // L 전용 회전 프리즘 테두리(cosc-pwrap)
  idleEligible: boolean;     // idle 애니 대상 여부 — static(N/R)은 false(무의미 리페인트 차단)
  seed: CardSeed;
  layers: FxLayerSpec[];
  signature: SignatureFx | null;
  particles: ParticleSpec[];
  /** 검산용: idle 시 동시 애니 노드 수 = 애니 레이어 + 카드측(artbg·명판·플레이버·테두리) + 파티클 */
  animatedNodeCount: number;
}

const TIER_OF: Record<Grade, FxTier> = { N: 'static', R: 'static', E: 'holo', L: 'prism' };

export function resolveCardFx(input: CardFxInput): CardFxSpec {
  const { grade, slug, level = 0, season = false } = input;
  const tier = TIER_OF[grade] ?? 'static';
  const isL = tier === 'prism';
  // 변형 우선순위(기존 컴포넌트 규칙 보존): +10 승격 > 시즌 한정 > 기본 다이아
  const variant: PrismVariant = isL ? (level >= 10 ? 'v6' : season ? 'v3' : 'v1') : '';
  const seed = cardSeed(input.seedId || `${grade}:${slug}`);

  const layers = [...TIER_LAYERS[tier]];
  if (variant === 'v6') layers.splice(4, 0, SHEEN2); // 기존 JSX 순서: sheen 다음·glare 앞

  const signature = isL ? SIGNATURE_FX[slug] || null : null;
  const particles = signature ? buildParticles(signature, seed.u) : [];

  // 카드측 상시 애니 노드: L = artbg+명판+플레이버+테두리(4) / E = artbg(1) / N·R = 0
  const hostAnimated = isL ? 4 : tier === 'holo' ? 1 : 0;
  const animatedNodeCount =
    hostAnimated + layers.filter((l) => l.animated).length + particles.length;

  return {
    tier, variant, prismBorder: isL, idleEligible: tier !== 'static',
    seed, layers, signature, particles, animatedNodeCount,
  };
}

/** 예산 검증 — 위반 목록 반환(빈 배열 = 통과). 테스트가 전 조합에 강제 */
export function assertBudget(spec: CardFxSpec): string[] {
  const v: string[] = [];
  if (spec.layers.length > PERF_BUDGET.maxFxLayers)
    v.push(`레이어 ${spec.layers.length} > 상한 ${PERF_BUDGET.maxFxLayers}`);
  if (spec.particles.length > PERF_BUDGET.maxParticles)
    v.push(`파티클 ${spec.particles.length} > 상한 ${PERF_BUDGET.maxParticles}`);
  if (spec.animatedNodeCount > PERF_BUDGET.maxAnimatedNodes)
    v.push(`애니 노드 ${spec.animatedNodeCount} > 상한 ${PERF_BUDGET.maxAnimatedNodes}`);
  for (const p of spec.particles)
    if (p.durS / (spec.signature?.speed || 1) < PERF_BUDGET.minAnimDurS)
      v.push(`파티클 주기 ${p.durS}s < 최소 ${PERF_BUDGET.minAnimDurS}s`);
  // 시그니처 선언 자체 검증(설계 §3) — 오타·미등록 조합을 스펙 산출 단계에서 조기 검출
  const sig = spec.signature;
  if (sig) {
    if (!REGISTERED_MOTIONS.includes(sig.motion))
      v.push(`미등록 motion '${sig.motion}' (등록: ${REGISTERED_MOTIONS.join('/')})`);
    if (!REGISTERED_SHAPES.includes(sig.shape))
      v.push(`미등록 shape '${sig.shape}' (등록: ${REGISTERED_SHAPES.join('/')})`);
    if (sig.colors.length === 0) v.push('시그니처 colors 빈 배열');
    const durLo = (sig.durRange ?? DEFAULT_DUR_RANGE)[0];
    if (durLo / (sig.speed || 1) < PERF_BUDGET.minAnimDurS)
      v.push(`durRange 하한 ${durLo}s / speed ${sig.speed} < 최소 ${PERF_BUDGET.minAnimDurS}s`);
  }
  return v;
}
